import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Link from '@zg-rentals/multibrand-ui/lib/Link';
import RentPaymentsRow from './RentPaymentsRow';
import OneTimePaymentsRow from './OneTimePaymentsRow';
import MoveInCostsRow from './MoveInCostsRow';
import RentalProtectionPremiumRow from './RentalProtectionPremiumRow';
import TransactionConstants from '@common/constants/TransactionConstants';
import StringConstants from '@common/constants/StringConstants';
import type { IFeaturePermissionModel, IPropertyInfoModel, ITransactionModel } from '@rental-manager/models/Types';
import Bell from '@zg-rentals/multibrand-ui/lib/icons/Bell';

type Props = {
  userEvent: Record<string, any>;
  category: string;
  isPaymentPlanRequested?: boolean;
  transactions: Array<ITransactionModel>;
  featurePermissions: IFeaturePermissionModel;
  resendInviteToRenter: ({ tenantEmail }: Record<string, any>) => void;
  requestRemindRenterPayRent: ({ tenantEmail }: Record<string, any>) => void;
  remindTenantToPayMIC: ({ tenantEmail }: Record<string, any>) => void;
  remindTenantToPayOneTimePayment: ({ tenantEmail, requestKey }: Record<string, any>) => void;
  cancelOneTimePayment: ({
    otpKey,
    tenantFirstName,
    paymentType,
    paymentPlanInstallmentTitle,
  }: Record<string, string>) => Promise<any>;
  goToCancelMoveInCostsModal: ({ groupKey, tenantFirstName, isMICAllowed, term }: any) => Promise<void>;
  currentTerm: IPropertyInfoModel;
  nextTerm: IPropertyInfoModel;
  listingAlias: string;
  goToSetupPaymentPlan: () => void;
  paymentPlanEnabled: boolean;
};

class TransactionRows extends React.Component<Props> {
  static defaultProps = {
    isPaymentPlanRequested: false,
  };
  renderTransactions = (): React.ReactNode => {
    const { category, isPaymentPlanRequested } = this.props;
    return (
      <div>
        {category === 'pastDue' && isPaymentPlanRequested && (
          <>
            <div className="pal pbs">
              <Bell colorType="action" size="md" className="bell-icon-alignment" />
              <Text inline>Past due amount doesn’t reflect any payment plan installments your tenant has made.</Text>
            </div>
            <div className="Dashboard-Row-HR" />
          </>
        )}
        {this.props.transactions.map((transaction, i: number) => {
          const associatedProps = {
            today: new Date(),
          };

          return (
            <div key={i.toString()}>
              {i !== 0 && <div className="Dashboard-Row-HR" />}
              <div className="Dashboard-Row">
                {transaction.paymentCategory === TransactionConstants.CATEGORY.RENT_PAYMENT && (
                  <RentPaymentsRow transaction={transaction} {...this.props} {...associatedProps} />
                )}
                {transaction.paymentCategory === TransactionConstants.CATEGORY.ONE_TIME_PAYMENT && (
                  <OneTimePaymentsRow transaction={transaction} {...this.props} {...associatedProps} />
                )}
                {transaction.paymentCategory === TransactionConstants.CATEGORY.MOVE_IN_COSTS && (
                  <MoveInCostsRow transaction={transaction} {...this.props} {...associatedProps} />
                )}
                {transaction.paymentCategory === TransactionConstants.CATEGORY.RENTAL_PROTECTION_DIRECT_DEBIT &&
                  transaction.rentalProtectionCosts.map((rProTransaction, idx: number) => {
                    return (
                      <>
                        {idx !== 0 && <div className="Dashboard-Row-HR" />}
                        <RentalProtectionPremiumRow
                          transaction={transaction}
                          rProTransaction={rProTransaction}
                          {...this.props}
                          {...associatedProps}
                        />
                      </>
                    );
                  })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderEmptyLedger = (): React.ReactNode => {
    const { category } = this.props;
    let title: string;
    let body: string;
    if (category === 'upcoming') {
      title = 'No upcoming payments';
      body = 'Payments that are due within the next month will appear here.';
    } else if (category === 'completed') {
      title = 'No completed payments...yet!';
      body = 'Look here to find payments that have been received or initiated.';
    } else {
      title = 'No past due payments';
      body = 'Payments that have not been initiated by their payment date will appear here.';
    }
    return (
      <div>
        <div className="Dashboard-Row-HR" />
        <div className="Dashboard-Row txtC pam inset-wrapper">
          <div className="inset inset-lg">
            <Text type="h2">{title}</Text>
            <Text lowlight>
              {body}&nbsp;
              <Link type="secondary" to={StringConstants.LINKS.FAQ_PAGE.zrm} target="_blank">
                Learn more
              </Link>
            </Text>
          </div>
        </div>
      </div>
    );
  };

  render(): React.ReactNode {
    const { transactions } = this.props;
    return transactions.length <= 0 ? this.renderEmptyLedger() : this.renderTransactions();
  }
}
export default TransactionRows;
